import type { Dispatch, ReactNode, SetStateAction } from "react";
import { createContext, useContext, useState } from "react";

export type VoiceNameInfo = {
  country: string;
  gender: string;
  url: string;
  voiceName: string;
};

type DurationChangeEvent = (duration: number) => void;

type TextToSpeechPlayerCollapseStatus = "collapsed" | "expanded-by-default" | "expanded-by-user";
type TextToSpeechPlayerStatus = "ended" | "hidden" | "paused" | "playing";

const initialTextToSpeechPlayerState = {
  collapseStatus: "expanded-by-default" as TextToSpeechPlayerCollapseStatus,
  controls: false,
  duration: 0,
  entityId: "",
  light: false,
  loaded: 0,
  loadedSeconds: 0,
  playbackRate: 1,
  played: 0,
  seeking: false,
  selectedVoiceIndex: 0,
  shouldShowPlaybackRateRadioButtons: false,
  shouldShowVoiceList: false,
  status: "hidden" as TextToSpeechPlayerStatus,
  thumbnail: "",
  title: "",
  voiceNameInfoList: [] as VoiceNameInfo[],
};

export type TextToSpeechPlayerState = Omit<typeof initialTextToSpeechPlayerState, "src"> & {
  src?: string;
};

type ContextValue = {
  durationChangeEventQueue: DurationChangeEvent[];
  setDurationChangeEventQueue: Dispatch<SetStateAction<DurationChangeEvent[]>>;
  setTextToSpeechPlayerState: Dispatch<SetStateAction<TextToSpeechPlayerState>>;
  textToSpeechPlayerState: TextToSpeechPlayerState;
};

const ArticleSpeechContext = createContext<ContextValue>({
  durationChangeEventQueue: [],
  setDurationChangeEventQueue: () => {},
  setTextToSpeechPlayerState: () => {},
  textToSpeechPlayerState: initialTextToSpeechPlayerState,
});

type Props = {
  children: ReactNode;
};

export const ArticleSpeechContextProvider: React.FunctionComponent<Props> = ({ children }) => {
  const [textToSpeechPlayerState, setTextToSpeechPlayerState] = useState(
    initialTextToSpeechPlayerState,
  );

  const [durationChangeEventQueue, setDurationChangeEventQueue] = useState(
    [] as DurationChangeEvent[],
  );

  return (
    <ArticleSpeechContext.Provider
      value={{
        durationChangeEventQueue,
        setDurationChangeEventQueue,
        setTextToSpeechPlayerState,
        textToSpeechPlayerState,
      }}
    >
      {children}
    </ArticleSpeechContext.Provider>
  );
};

ArticleSpeechContextProvider.displayName = "ArticleSpeechContextProvider";

export const useArticleSpeechContext = () => useContext(ArticleSpeechContext);
